import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import type { Address, Cart, ShippingUnavailability } from '@getjust/gateway';

import { CART_QUERY_KEY } from '$src/constants';
import { useBaseApiUrl } from '$src/hooks/client/useBaseApiUrl';
import { useCartIdUpdater } from '$src/hooks/client/useCartIdUpdater';
import { useCart, useCountriesFromCache } from '$src/hooks/queries';
import { UPSELL_QUERY_KEY } from '$src/hooks/queries/useUpsellProducts';
import { useCorrectionsAtom } from '$src/hooks/state/useCorrectionsAtom';
import { gateway, setXCountryGatewayHeader } from '$src/http';

export const UPDATE_SHIPPING_ADDRESS_MUTATION_KEY = 'MUTATION/UPDATE_SHIPPING_ADDRESS';

// Shopify CartUserError codes that indicate the buyer's address itself is
// the problem (as opposed to a transient backend / carrier failure). Mapped
// to `unavailability.reason: 'address_invalid'` with the offending fields so
// the UI can point the user at the right input.
const ADDRESS_FIELD_FOR_CODE: Record<string, string> = {
  NOT_SUPPORTED: 'country_code',
  INVALID_COUNTRY_CODE: 'country_code',
  INVALID_ZIP_CODE_FOR_COUNTRY: 'zip',
  INVALID_ZIP_CODE_FOR_PROVINCE: 'zip',
  ZIP_CODE_NOT_SUPPORTED: 'zip',
  INVALID_PROVINCE: 'province',
  INVALID_PHONE_NUMBER: 'phone',
};

type ShopifyUserError = { code?: string; field?: string | string[] | null; message?: string };

function classifyAddressError(
  userErrors: ShopifyUserError[] | undefined,
): ShippingUnavailability | undefined {
  if (!userErrors?.length) return undefined;
  const codes = userErrors.map((e) => e?.code).filter((c): c is string => Boolean(c));
  const invalidFields = Array.from(
    new Set(
      userErrors
        .map((e) => ADDRESS_FIELD_FOR_CODE[e?.code ?? ''])
        .filter((f): f is string => Boolean(f)),
    ),
  );
  return {
    reason: 'address_invalid',
    details: {
      invalidFields: invalidFields.length ? invalidFields : undefined,
      errorCodes: codes.length ? codes : undefined,
    },
  };
}

export const useUpdateShippingAddress = () => {
  const { initCorrections } = useCorrectionsAtom();
  const queryClient = useQueryClient();
  const baseUrl = useBaseApiUrl();
  const countries = useCountriesFromCache();
  const { data: cart } = useCart();
  const updateCartId = useCartIdUpdater();

  const mutation = useMutation({
    mutationFn: ({ address, email }: { address: Address; email: string; alreadyRetried?: boolean }) => {
      const countryCodeContext =
        (address?.countryCode && address?.countryCode.length !== 0 ? address?.countryCode : undefined) ??
        countries.find((country) => country.country === address?.country)?.value;
      return gateway.post<Cart>(`${baseUrl}/update-shipping-address`, { address, email, countryCodeContext });
    },
    retry: 2,
    mutationKey: [UPDATE_SHIPPING_ADDRESS_MUTATION_KEY, baseUrl],
    onMutate: async ({ address }: { address: Address; email: string; alreadyRetried?: boolean }) => {
      const previousCart = queryClient.getQueryData<Cart>([CART_QUERY_KEY, cart?.id]);
      const newCart = { ...previousCart, shipping: { address, ...previousCart?.shipping } };
      // @ts-expect-error - known
      queryClient.setQueryData<Cart>([CART_QUERY_KEY, previousCart?.id], newCart);
      // Return a context object with the snapshotted value
      return { previousCart };
    },
    onSuccess: ({ data }, _, context) => {
      updateCartId(data?.id);

      queryClient.setQueryData<Cart>([CART_QUERY_KEY, data.id], data);
      if (data.corrections?.length) {
        initCorrections(data.corrections);
      }
      const countryCode = data.shipping.address?.countryCode;
      const oldCountryCode = context?.previousCart?.shipping?.address?.countryCode;

      if (countryCode) {
        setXCountryGatewayHeader(countryCode.toLocaleUpperCase());
        // Invalidate upsell products query when country changes since it affects available products/currency
        if (oldCountryCode !== countryCode) {
          queryClient.invalidateQueries({ queryKey: [UPSELL_QUERY_KEY] });
        }
      }
    },
    onError: (error: unknown, _, context) => {
      queryClient.setQueryData<Cart>([CART_QUERY_KEY, context?.previousCart?.id], context?.previousCart);

      if (!cart) return;

      const userErrors: ShopifyUserError[] | undefined =
        error instanceof AxiosError ? error?.response?.data?.error : undefined;
      const unavailability: ShippingUnavailability =
        classifyAddressError(userErrors) ?? { reason: 'service_error' };

      // Synthesize a cart that the existing render gate
      // (`cart.shipping.ready && !methods.length`) catches, but now carries a
      // reason the UI can branch on. We keep `shipping.address` truthy via the
      // previous-cart address so the form doesn't snap back to "enter your
      // address" copy on a transient failure.
      const cartWithUnavailability: Cart = {
        ...(cart as Cart),
        shipping: {
          ...cart.shipping,
          address: cart.shipping.address,
          ready: Boolean(cart.shipping.ready),
          methods: [],
          selected: undefined,
          unavailability,
        },
      };
      queryClient.setQueryData<Cart>([CART_QUERY_KEY, cartWithUnavailability.id], cartWithUnavailability);
    },
  });

  return { updateShippingAddress: mutation.mutateAsync, state: mutation.status, reset: mutation.reset };
};
